import L from 'leaflet'
import { shapeVertices, type ShapeKind } from './symbology.ts'

export interface ShapeMarkerOptions extends L.CircleMarkerOptions {
  shape?: ShapeKind
}

/** Leaflet internals this class depends on. `_point`, `_parts`, `_renderer`,
 *  and `_updatePoly` are private and absent from @types/leaflet, so they are
 *  declared here rather than via a global augmentation — the unsoundness stays
 *  contained to this file and is visible to anyone reading it. The contract is
 *  pinned by the real-renderer tests in ShapeMarker.test.ts. */
interface PathInternals {
  _point: L.Point
  _radius: number
  _parts: L.Point[][]
  _renderer: { _updatePoly(layer: unknown, closed: boolean): void }
  _updatePath(): void
}

/**
 * A fixed-pixel-size polygon marker.
 *
 * Extends CircleMarker (not Polygon) so the symbol keeps its size across zoom
 * levels and inherits click handling, tooltip binding, setStyle, and the
 * circular _containsPoint — a good hit-test approximation for small targets.
 *
 * Both renderers read the vertex ring off `layer._parts` rather than taking it
 * as an argument, so assigning `_parts` before dispatching is required, not
 * incidental (leaflet/src/layer/vector/Canvas.js:280, SVG.js:165).
 */
export class ShapeMarker extends L.CircleMarker {
  _updatePath(): void {
    const self = this as unknown as PathInternals
    const shape = (this.options as ShapeMarkerOptions).shape ?? 'circle'

    if (shape === 'circle') {
      // Meters stay on Leaflet's own arc path — the fastest route for the
      // most numerous layer.
      ;(L.CircleMarker.prototype as unknown as PathInternals)._updatePath.call(this)
      return
    }

    const verts = shapeVertices(shape, self._point.x, self._point.y, self._radius)
    self._parts = [verts.map(([x, y]) => L.point(x, y))]
    self._renderer._updatePoly(this, true)
  }
}

/** Factory mirroring L.circleMarker()'s signature. `options` defaults to `{}`
 *  because @types/leaflet declares CircleMarker's constructor `options`
 *  parameter as required (only the L.circleMarker() factory marks it
 *  optional), even though every field of CircleMarkerOptions is itself
 *  optional and Leaflet's runtime handles a missing options object fine. */
export function shapeMarker(
  latlng: L.LatLngExpression,
  options?: ShapeMarkerOptions,
): ShapeMarker {
  return new ShapeMarker(latlng, options ?? {})
}
